import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  getUserDataApi,
  editUserProfileApi,
  getUsersListApi,
  addUserApi,
  deleteUserApi,
  updateUserApi,
  toggleUserStatusApi,
  setDefaultTenantApi,
} from '../../api/users';
import { AuthenticationService } from './auth.service';
import { UsersResponse } from '../models/users.model';
import { getCapabilities } from '../models/capabilities.model';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UsersService {
  user: any;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private translateSerive: TranslateService
  ) {}

  /**
   * Obtener datos de usuario
   * @param token authorization token
   */
  getUserData(token: string): Observable<any> {
    return getUserDataApi(this.http, token);
  }

  /**
   * Editar perfil de usuario
   * @param data User data
   */
  editUserProfile(data: {
    name?: string;
    surname?: string;
    phone?: string;
    password?: string;
    notificationPush?: boolean;
    notificationEmail?: boolean;
  }): Observable<any> {
    const token = this.authService.getToken();
    return editUserProfileApi(this.http, token!, data);
  }

  /**
   * Obtener lista de usuarios
   * @param queryParams Query parameters for filtering or pagination
   */
  getUsersList(queryParams: any = {}): Observable<UsersResponse> {
    return getUsersListApi(
      this.http,
      this.authService.getToken()!,
      queryParams
    ).pipe(
      map((response: UsersResponse) => {
        if (response.data?.items) {
          response.data.items = response.data.items.map((record) => ({
            ...record,
            capabilities: (record.capabilities as unknown as string[]).map(
              (capKey: string) => {
                const capability = getCapabilities(this.translateSerive).find(
                  (c) => c.key === capKey
                );
                return capability || { key: capKey, label: capKey };
              }
            ),
          }));
        }
        return response;
      })
    );
  }

  /**
   * Añadir usuario
   * @param data
   */
  addUser(data: {
    name: string;
    surname: string;
    email: string;
    phone: string;
    capabilities: string[];
  }): Observable<any> {
    const token = this.authService.getToken();
    return addUserApi(this.http, token!, data);
  }

  /**
   * Eliminar usuario
   * @param id User id
   */
  deleteUser(id: string): Observable<any> {
    const token = this.authService.getToken();
    return deleteUserApi(this.http, token!, id);
  }

  /**
   * Editar usuario
   * @param id User ID
   * @param data
   */
  updateUser(
    id: string,
    data: {
      name?: string;
      surname?: string;
      phone?: string;
      capabilities?: string[];
    }
  ): Observable<any> {
    const token = this.authService.getToken();
    return updateUserApi(this.http, token!, id, data);
  }

  /**
   * Cambiar estado de usuario
   * @param id User ID
   */
  toggleUserStatus(id: number): Observable<any> {
    const token = this.authService.getToken();
    return toggleUserStatusApi(this.http, token!, id);
  }

  /**
   * Establecer el proveedor por defecto
   * @param id Provider ID
   */
  setDefaultTenant(id: number): Observable<any> {
    const token = this.authService.getToken();
    return setDefaultTenantApi(this.http, token!, id);
  }
}
